import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A tela cheia de um pedaço da tela (03/11/2026, o Mapa do time do Sinistro).
 *
 * O estado é lido do `fullscreenchange`, e não do clique: sair pelo Esc, ou
 * pelo gesto do sistema, não passa pelo botão, e o rótulo ficaria dizendo
 * "Sair da tela cheia" fora dela.
 *
 * Onde o navegador não tem tela cheia de elemento — o Safari do iPhone só a dá
 * a vídeo — ou recusa o pedido, o pedaço ocupa a janela inteira por CSS
 * (`pseudo`), com o Esc fechando do mesmo jeito. O botão nunca fica sem efeito.
 */
export function useFullscreen<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [native, setNative] = useState(false);
  const [pseudo, setPseudo] = useState(false);

  useEffect(() => {
    const sync = () =>
      setNative(ref.current !== null && document.fullscreenElement === ref.current);
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  /* Na tela cheia por CSS, a página de trás não rola, e o Esc fecha. */
  useEffect(() => {
    if (!pseudo) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPseudo(false);
    };
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [pseudo]);

  const toggle = useCallback(() => {
    if (pseudo) {
      setPseudo(false);
      return;
    }
    if (document.fullscreenElement) {
      void document.exitFullscreen();
      return;
    }
    const el = ref.current;
    if (el && typeof el.requestFullscreen === 'function') {
      el.requestFullscreen().catch(() => setPseudo(true));
    } else {
      setPseudo(true);
    }
  }, [pseudo]);

  return { ref, active: native || pseudo, pseudo, toggle };
}
